import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Building2, User, AlertCircle, Gift, CheckCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { getStoredReferral, storeIncomingReferral, validateReferralCode } from "../../utils/referrals";
import toast from "react-hot-toast";

const BANKS = ["Access Bank","First Bank","GTBank","UBA","Zenith Bank","Stanbic IBTC","FCMB","Fidelity Bank","Polaris Bank","Wema Bank","Kuda Bank","Opay","PalmPay","Other"];

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [accountType, setAccountType] = useState("customer");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referrerStatus, setReferrerStatus] = useState(null); // { valid, name }

  const urlRef = searchParams.get("ref");
  const initialRef = (urlRef || getStoredReferral() || "").trim().toUpperCase();

  const [form, setForm] = useState({
    fullName: "", email: "", phone: "", password: "", confirmPassword: "",
    businessName: "", businessAddress: "", bankName: "", accountNumber: "", accountName: "",
    referralCode: initialRef,
  });

  // If a referral code was passed via URL, store it and check validity
  useEffect(() => {
    if (urlRef) {
      storeIncomingReferral(urlRef);
    }
    if (initialRef) {
      setShowReferralInput(true);
      validateReferralCode(initialRef).then(res => {
        if (res.valid) {
          setReferrerStatus({ valid: true, name: res.referrerName });
        }
      });
    }
  }, [urlRef, initialRef]);

  const set = (k) => (e) => {
    const val = k === "referralCode" ? e.target.value.toUpperCase() : e.target.value;
    setForm(f => ({ ...f, [k]: val }));
    if (k === "referralCode") {
      if (val.trim().length >= 4) {
        validateReferralCode(val.trim()).then(res => {
          if (res.valid) {
            setReferrerStatus({ valid: true, name: res.referrerName });
          } else {
            setReferrerStatus({ valid: false, error: res.error });
          }
        });
      } else {
        setReferrerStatus(null);
      }
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.password !== form.confirmPassword) return toast.error("Passwords don't match");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setLoading(true);
    try {
      await register(form.email, form.password, {
        ...form,
        accountType,
        referredByCode: form.referralCode?.trim() || null,
      });
      toast.success("Account created! Welcome to Afriport 🎉");
      navigate(accountType === "business" ? "/business/submit" : "/shop");
    } catch (err) {
      toast.error(err.message.includes("email-already-in-use") ? "Email already registered" : "Registration failed. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--surface)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 20px" }}>
      <div style={{ width: "100%", maxWidth: 560 }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <Link to="/" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 24, textDecoration: "none" }}>
            <img src="/images/Afriportstore.png" alt="Afriport Logo" style={{ height: 52, width: "auto", objectFit: "contain" }} />
          </Link>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 30, fontWeight: 700 }}>Create your account</h1>
          <p style={{ color: "var(--text-muted)", marginTop: 8 }}>
            Already have one? <Link to="/login" style={{ color: "var(--brand)", fontWeight: 500 }}>Sign in</Link>
          </p>
        </div>

        {/* Account type selector */}
        <div className="responsive-two-col" style={{ gap: 12, marginBottom: 28 }}>
          {[
            { type: "customer", icon: User, label: "Personal", desc: "Shop for yourself" },
            { type: "business", icon: Building2, label: "Business", desc: "Sell products, earn 90%" },
          ].map(({ type, icon: Icon, label, desc }) => (
            <button key={type} type="button" onClick={() => setAccountType(type)}
              style={{
                padding: "16px", border: `2px solid ${accountType === type ? "var(--brand)" : "var(--border)"}`,
                borderRadius: 12, background: accountType === type ? "var(--brand-light)" : "#fff",
                cursor: "pointer", textAlign: "left", transition: "all 0.2s",
              }}>
              <Icon size={22} color={accountType === type ? "var(--brand)" : "var(--text-muted)"} />
              <div style={{ fontWeight: 600, fontSize: 15, marginTop: 8, color: "var(--text)" }}>{label}</div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>{desc}</div>
            </button>
          ))}
        </div>

        {/* Business notice */}
        {accountType === "business" && (
          <div style={{ display: "flex", gap: 12, padding: 16, background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 12, marginBottom: 24 }}>
            <AlertCircle size={18} color="#1D4ED8" style={{ flexShrink: 0, marginTop: 2 }} />
            <p style={{ fontSize: 14, color: "#1E40AF", lineHeight: 1.6 }}>
              <strong>Business terms:</strong> Afriport retains <strong>10%</strong> commission on each sale. The remaining <strong>90%</strong> is automatically deposited to your bank account on file. You can submit products for review — Afriport admins approve and publish all listings.
            </p>
          </div>
        )}

        <div className="card" style={{ padding: "clamp(18px, 4vw, 32px)" }}>
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Basic info */}
            <div className="responsive-two-col">
              <div className="form-group">
                <label>Full Name</label>
                <input className="form-input" placeholder="John Doe" required value={form.fullName} onChange={set("fullName")} />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input className="form-input" placeholder="+234 812 345 6789" value={form.phone} onChange={set("phone")} />
              </div>
            </div>

            <div className="form-group">
              <label>Email Address</label>
              <input className="form-input" type="email" placeholder="you@example.com" required value={form.email} onChange={set("email")} />
            </div>

            <div className="responsive-two-col">
              <div className="form-group">
                <label>Password</label>
                <div style={{ position: "relative" }}>
                  <input className="form-input" type={showPwd ? "text" : "password"} placeholder="Min 8 chars" required style={{ paddingRight: 44 }}
                    value={form.password} onChange={set("password")} />
                  <button type="button" onClick={() => setShowPwd(!showPwd)}
                    style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", color: "var(--text-muted)", cursor: "pointer" }}>
                    {showPwd ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <input className="form-input" type="password" placeholder="Repeat password" required value={form.confirmPassword} onChange={set("confirmPassword")} />
              </div>
            </div>

            {/* Business fields */}
            {accountType === "business" && (
              <>
                <div style={{ borderTop: "1px solid var(--border)", paddingTop: 20 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 16 }}>Business Information</p>
                </div>
                <div className="form-group">
                  <label>Business Name</label>
                  <input className="form-input" placeholder="Acme Electronics Ltd" required value={form.businessName} onChange={set("businessName")} />
                </div>
                <div className="form-group">
                  <label>Business Address</label>
                  <input className="form-input" placeholder="123 Trade Road, Lagos" value={form.businessAddress} onChange={set("businessAddress")} />
                </div>

                <div style={{ borderTop: "1px solid var(--border)", paddingTop: 20 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 16 }}>Bank Details (for payouts)</p>
                </div>
                <div className="responsive-two-col">
                  <div className="form-group">
                    <label>Bank Name</label>
                    <select className="form-select" required value={form.bankName} onChange={set("bankName")}>
                      <option value="">Select bank</option>
                      {BANKS.map(b => <option key={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Account Number</label>
                    <input className="form-input" placeholder="0123456789" maxLength={10} required value={form.accountNumber} onChange={set("accountNumber")} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Account Name</label>
                  <input className="form-input" placeholder="As it appears on bank records" required value={form.accountName} onChange={set("accountName")} />
                </div>
              </>
            )}

            {/* Referral Code section */}
            <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
              {!showReferralInput && !form.referralCode ? (
                <button
                  type="button"
                  onClick={() => setShowReferralInput(true)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "var(--brand)",
                    fontSize: 13,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <Gift size={15} /> Have a referral code from a friend?
                </button>
              ) : (
                <div className="form-group">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                      <Gift size={14} color="var(--brand)" /> Referral Code (Optional)
                    </label>
                    {!urlRef && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowReferralInput(false);
                          setForm(f => ({ ...f, referralCode: "" }));
                          setReferrerStatus(null);
                        }}
                        style={{ border: "none", background: "transparent", fontSize: 12, color: "var(--text-muted)", cursor: "pointer" }}
                      >
                        Hide
                      </button>
                    )}
                  </div>
                  <div style={{ position: "relative" }}>
                    <input
                      className="form-input"
                      placeholder="e.g. AFR-JOHN42"
                      value={form.referralCode}
                      onChange={set("referralCode")}
                      style={{
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        fontWeight: 600,
                        borderColor: referrerStatus?.valid ? "#10B981" : referrerStatus?.error ? "#EF4444" : undefined,
                      }}
                    />
                    {referrerStatus?.valid && (
                      <CheckCircle size={18} color="#10B981" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }} />
                    )}
                  </div>

                  {referrerStatus?.valid && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 12, color: "#047857", background: "rgba(16, 185, 129, 0.1)", padding: "6px 10px", borderRadius: 6 }}>
                      <CheckCircle size={13} />
                      <span>Referred by <strong>{referrerStatus.name}</strong>. Welcome perks will be applied!</span>
                    </div>
                  )}

                  {referrerStatus?.error && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 12, color: "#DC2626" }}>
                      <AlertCircle size={13} />
                      <span>{referrerStatus.error}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button className="btn btn-primary btn-lg w-full" type="submit" disabled={loading} style={{ marginTop: 8 }}>
              {loading ? "Creating account…" : `Create ${accountType === "business" ? "Business" : ""} Account`}
            </button>
          </form>
        </div>

        <p style={{ textAlign: "center", fontSize: 13, color: "var(--text-muted)", marginTop: 20 }}>
          By registering, you agree to Afriport's <Link to="/terms" style={{ color: "var(--brand)" }}>Terms of Service</Link> and <Link to="/privacy" style={{ color: "var(--brand)" }}>Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
